import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Box, Button,
    Grid, Popover,
    SelectChangeEvent,
    Typography
} from "@mui/material";
import { useTheme } from "../../contexts/theme";
import {Clip, Highlight} from "../../types/clipTypes";
import StyledDropdown from "../CommonComponents/StyledDropdown/StyledDropdown";
import ClipListElem from "../ClipsComponents/ClipListElem/ClipListElem";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import StyledCheckBox from "../CommonComponents/StyledCheckbox/StyledCheckBox";
import { formatDuration, formatUTCDate } from "../../utils/dateTimeutils";
import StyledSwitch from "../CommonComponents/StyledSwitch/StyledSwitch";
import StyledRangeSlider from "../CommonComponents/StyledRangeSlider/StyeledRangeSlider";
import { LoadError, Song } from "../../types/generic";
import ErrorSnackBar from "../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import StyledPopup from "../CommonComponents/StyledPopup/StyledPopup";
import {createHighlight, getPistoleroOptions, suggestClips} from "../../queries/highlightQueries";
import { useSelectedEvent } from "../../contexts/event";
import OpenerCloserComponent, {
    Competition,
    defaultCompetition
} from "../CommonComponents/OpenerCloserComponent/OpenerCloserComponent";
import { defaultOpenerCloserObject } from "../CommonComponents/OpenerCloserComponent/OpenerCloserContentComponent";
import AudioFadingCollapse from "../CommonComponents/AudioFadingCollapse/AudioFadingCollapse";
import {
    defaultFadeDuration,
    fadeDurationStep,
    maxAudioFadeDuration,
    minAudioFadeDuration,
    visibleHighlightTypes
} from "../../utils/constants";
import {OpenerCloserObject} from "../../types/opernerCloserTypes";
import StyledCollapse from "../CommonComponents/StyledCollapse/StyledCollapse";
import {HighlightType} from "../../types/highlightTypes";
import SongSelection from "../CommonComponents/SongSelection/SongSelection";
import {getJobGroupedFastClips} from "../../queries/matchPageQueries";
import {sortClipByEventStream} from "../../utils/utilsClip";
import WarningPopover from "../CommonComponents/WarningPopover/WarningPopover";
import EditorPopup from "../EditorPopup/EditorPopup";
import StyledSnackbar from "../CommonComponents/StyledSnackbar/StyledSnackbar";

interface HighlightPopupProps {
    isOpen: boolean;
    handleClose: () => void;
    clips: Clip[],
    setCreationLoading: (state: 'Success' | 'Start' | 'Failed') => void;
}

type GroupedClips = {
    [key: string]: Clip[]
}

interface ProductionId {
    label: string,
    id: string,
}

type JobGroupedFastClips = Record<string, Clip[]>

const maxDuration = 480
const minDuration = -480

const moreHighlightTypes = visibleHighlightTypes.length > 1
const needFetchFastClips = process.env.REACT_APP_SPORT === 'basketball';

const HighlightPopup: React.FC<HighlightPopupProps> = ({ setCreationLoading, isOpen, handleClose, clips }) => {

    const theme = useTheme();
    const { selectedEvent } = useSelectedEvent();
    const [selectedProdId, setSelectedProdId] = useState<ProductionId>({ label: '', id: '' });
    const [productionIds, setProductionIds] = useState<ProductionId[]>([]);
    const [groupedClips, setGroupedClips] = useState<GroupedClips>({});
    const [onlySelected, setOnlySelected] = useState<boolean>(false);
    const [autoSelectionDuration, setAutoSelectionDuration] = useState<[number, number]>([-120, 180]);
    const [loadError, setLoadError] = useState<LoadError>({ error: false, message: '' })
    const [audioFading, setAudioFading] = useState<boolean>(false)
    const [audioFadingDuration, setAudioFadingDuration] = useState<number>(defaultFadeDuration)

    const [selectedCompetition, setSelectedCompetition] = useState<Competition>({ id: undefined, name: "", type: "", season: "" })

    // opener-closer
    const [selectedOpener, setSelectedOpener] = useState<OpenerCloserObject>(defaultOpenerCloserObject);
    const [selectedCloser, setSelectedCloser] = useState<OpenerCloserObject>(defaultOpenerCloserObject);

    const [isHighlightSettingOpen, setIsHighlightSettingOpen] = useState<boolean>(false);
    const [selectedHighlight, setSelectedHighlight] = useState<HighlightType>({type: 'Regular', label: 'Regular'})
    const [selectedSong, setSelectedSong] = useState<Song>()
    const [pistoleroOptions, setPistoleroOptions] = useState<string[]>([])
    const [pistolero, setPistolero] = useState<string>('')
    const [jobGroupedFastClips, setJobGroupedFastClips] = useState<JobGroupedFastClips>({})

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showEditingWarning, setShowEditingWarning] = useState<boolean>(false);
    const createHighlightButtonRef = useRef<HTMLButtonElement>(null);

    const [editorClip, setEditorClip] = useState<Clip | undefined | Highlight>(undefined);
    const [editorOpen, setEditorOpen] = useState<boolean>(false)
    const [isClipDownloading, setIsClipDownloading] = React.useState<string>('NODOWNLOAD');

    const [selectedClips, setSelectedClips] = useState<Set<string>>(new Set<string>())

    useEffect(() => {
        if (showEditingWarning && createHighlightButtonRef.current) {
            setAnchorEl(createHighlightButtonRef.current);
        } else {
            setAnchorEl(null);
        }
    }, [showEditingWarning]);


    useEffect(() => {
        if(selectedEvent && selectedEvent.competition) {
            setSelectedCompetition({name: selectedEvent.competition.name, id: selectedEvent.competition.id, type: "", season: ""})
        } else {
            setSelectedCompetition(defaultCompetition)
        }
    }, [selectedEvent]);

    async function loadFastClips(){
        if(selectedEvent){
            try{
                const fastClips = await getJobGroupedFastClips(selectedEvent?.id)
                setJobGroupedFastClips(fastClips)
            }catch (e) {
                console.error("Error fetching fast clip", e)
            }
        }
    }

    useEffect(() => {
        if(isOpen && needFetchFastClips){
            loadFastClips()
        }
        if (isOpen){
            loadPistoleroOptions()
        }
    }, [isOpen]);

    useEffect(() => {
        const tempGroupedClips: GroupedClips = {};
        const tempProductionIds: ProductionId[] = [];
        let maxJob: (ProductionId & { isoDate: string }) | null = null;

        if (clips && isOpen) {
            for (const clip of clips) {
                const highlightClip = clip;
                const extractionTime = highlightClip.job_extraction_schedule as string;

                // Parse extraction time as Date object
                const extractionDate = new Date(extractionTime);

                if (isNaN(extractionDate.getTime())) {
                    continue; // skip invalid dates
                }

                if (!tempGroupedClips.hasOwnProperty(highlightClip.job_id)) {
                    tempGroupedClips[highlightClip.job_id] = [];
                    tempProductionIds.push({ label: formatUTCDate(extractionTime, false), id: highlightClip.job_id });

                    if (!maxJob) {
                        maxJob = { label: formatUTCDate(extractionTime, false), id: highlightClip.job_id, isoDate: extractionTime };
                    } else {
                        const maxJobDate = new Date(maxJob.isoDate);
                        if (extractionDate > maxJobDate) {
                            maxJob = { label: formatUTCDate(extractionTime, false), id: highlightClip.job_id, isoDate: extractionTime };
                        }
                    }
                }

                if (clip.latest_social_clip_id !== undefined && clip.latest_social_clip_id !== null) {
                    tempGroupedClips[highlightClip.job_id].push(highlightClip);
                }
            }
            for (const jobId in tempGroupedClips) {
                tempGroupedClips[jobId].sort((clip1, clip2) => sortClipByEventStream(clip1, clip2))
            }

            if (maxJob) {
                setSelectedProdId(maxJob);
            } else {
                console.warn("No maxJob found!");
            }

            setProductionIds(tempProductionIds);
            setGroupedClips(tempGroupedClips);
        }
    }, [isOpen, clips]);

    function handleProdIdChange(event: SelectChangeEvent) {
        const prodId = event.target.value as string;
        setSelectedProdId({ label: formatUTCDate(prodId, false), id: prodId });
        setPistoleroOptions([])
        setPistolero('')
        loadPistoleroOptions()
    }

    function modularClipSelection(
        clips: GroupedClips | JobGroupedFastClips,
        setClips: (updated: GroupedClips | JobGroupedFastClips) => void,
        clipIndex: number
    ){
        const clipId = clips[selectedProdId.id][clipIndex].id
        const tempSelectedClips = new Set(selectedClips)
        if (!tempSelectedClips.has(clipId)){
            tempSelectedClips.add(clipId)
        }else{
            tempSelectedClips.delete(clipId)
        }
        setSelectedClips(tempSelectedClips);
    }

    function handleClipSelection(clipIndex: number) {
        const isFast = selectedHighlight.type === 'Fast';
        modularClipSelection(
            isFast ? {...jobGroupedFastClips} : {...groupedClips},
            isFast ? setJobGroupedFastClips : setGroupedClips,
            clipIndex,
        );
    }

    function modularSetAll(
        clips: GroupedClips | JobGroupedFastClips,
        value: boolean,
        setClips: (updated: GroupedClips | JobGroupedFastClips) => void
    ){
        const updatedClips: GroupedClips = { ...clips };
        let tempSelectedClips = value ? new Set(selectedClips) : new Set<string>()
        if (value){
            for (const clip of updatedClips[selectedProdId.id]) {
                tempSelectedClips.add(clip.id)
            }
        }
        setSelectedClips(tempSelectedClips);
    }

    function handleSetAll(value: boolean) {
        const isFast = selectedHighlight.type === 'Fast';
        modularSetAll(
            isFast ? jobGroupedFastClips : groupedClips,
            value,
            isFast ? setJobGroupedFastClips : setGroupedClips
        );

    }

    function modularAutoSelection(
        clips: GroupedClips | JobGroupedFastClips,
        selectedClipIds: string[],
        setClips: (updated: GroupedClips | JobGroupedFastClips) => void
    ){
        const tempSelectedClips = new Set<string>()
        for (const clipId of selectedClipIds) {
            const clip = clips[selectedProdId.id].find(clip => clip.latest_social_clip_id === clipId);
            if (clip) {
                tempSelectedClips.add(clip.id)
            }
        }
        setSelectedClips(tempSelectedClips);
    }

    async function handleAutoSelection() {
        try {
            const response = await suggestClips([selectedProdId.id], selectedEvent ? [selectedEvent!.id] : [], Math.abs(autoSelectionDuration[1]), Math.abs(autoSelectionDuration[0]), selectedHighlight.type, pistolero);
            const selectedClipIds: string[] = response.clips;
            const isFast = selectedHighlight.type === 'Fast';
            modularAutoSelection(
                isFast ? {...jobGroupedFastClips} : {...groupedClips},
                selectedClipIds,
                isFast ? setJobGroupedFastClips : setGroupedClips
            )
        } catch (e) {
            setLoadError({ error: true, message: 'Auto selection service not available' });
        }
    }

    function handleOnlySelected(event: React.ChangeEvent<HTMLInputElement>) {
        setOnlySelected(event.target.checked)
    }

    function handleHighlightDurationChange(event: Event, newValue: number[] | number, activeThumb: number) {
        const valueChanged = newValue as [number, number];
        let [min, max] = valueChanged
        min = activeThumb === 0 && Math.abs(min) > Math.abs(max) - 30 ? -(max - 30) : min
        max = activeThumb === 1 && Math.abs(max) < Math.abs(min) + 30 ? -(min - 30) : max
        setAutoSelectionDuration([min, max]);
    }

    async function executeCreateHighlight(){
        try {
            const clips = selectedHighlight.type === 'Fast' ? jobGroupedFastClips : groupedClips;
            const sendSelectedClips: string[] = clips[selectedProdId.id].filter(clip => selectedClips.has(clip.id) && clip.latest_social_clip_id).map(clip => clip.latest_social_clip_id) as string[];
            setCreationLoading('Start');
            onClose();
            await createHighlight(sendSelectedClips, selectedOpener, selectedCloser, audioFading, audioFadingDuration, selectedHighlight.type, selectedSong, pistolero);
            setCreationLoading('Success');
        } catch (e) {
            setCreationLoading('Failed');
        }
    }

    function handleHighlightCreation() {
        if (areProcessingClipsSelected()){
            setShowEditingWarning(true)
        }else{
            executeCreateHighlight()
        }
    }

    function getHighlightNumber() {
        const clips = selectedHighlight.type === 'Fast' ? jobGroupedFastClips : groupedClips
        if (clips && clips[selectedProdId.id]) {
            return clips[selectedProdId.id].filter(clip => selectedClips.has(clip.id)).length
        } else {
            return 0
        }
    }

    function getHighlightDuration() {
        const clips = selectedHighlight.type === 'Fast' ? jobGroupedFastClips : groupedClips
        if (clips && clips[selectedProdId.id]) {
            return formatDuration(clips[selectedProdId.id].reduce((accumulator, clip) => (accumulator + (selectedClips.has(clip.id) ? 1 : 0) * (clip.latest_social_clip_duration ? clip.latest_social_clip_duration : 0)), 0))
        } else {
            return "00:00"
        }
    }

    function onClose(){
        setSelectedCloser(defaultOpenerCloserObject)
        setSelectedOpener(defaultOpenerCloserObject)
        setAudioFading(false);
        setAudioFadingDuration(defaultFadeDuration);
        setPistoleroOptions([])
        setPistolero('')
        setSelectedSong(undefined)
        setSelectedHighlight({type: 'Regular', label: 'Regular'})
        setSelectedClips(new Set<string>())
        handleClose();
    }

    function handleChangeHighlightType(highlightTypeId: string) {
        const highlightType = visibleHighlightTypes.find(type => type.type === highlightTypeId)
        if(highlightType){
            setSelectedHighlight(highlightType)
        }
    }

    async function loadPistoleroOptions(){
        if(selectedEvent){
            const pistoleros = await getPistoleroOptions(selectedEvent.id)
            setPistoleroOptions(pistoleros)
            if (pistoleros.length === 1){
                setPistolero(pistoleros[0])
            }
        }
    }

    function isCreateButtonDisabled(){
        const clipNumber = getHighlightNumber()
        return Boolean(selectedHighlight.type === 'Pistolero' ? !pistolero || clipNumber <= 0 : clipNumber <= 0)
    }

    function getClipsArray(){
        const clips = selectedHighlight.type === 'Fast' ? jobGroupedFastClips : groupedClips;
        const jobClips = (onlySelected ? clips[selectedProdId.id].filter((clip) => selectedClips.has(clip.id)) : clips[selectedProdId.id])
        return jobClips ? jobClips : []
    }

    function areProcessingClipsSelected(){
        const clips = selectedHighlight.type === 'Fast' ? jobGroupedFastClips : groupedClips;
        if(clips[selectedProdId.id]){
            const selectedEditingClips = clips[selectedProdId.id].filter(clip => selectedClips.has(clip.id) && clip.latest_social_clip_id && clip.editing)
            return selectedEditingClips.length > 0
        }else{
            return false
        }
    }

    function closeEditor(){
        setEditorOpen(false);
    }

    const onClipClick = useCallback((clip: Clip) => {
        if (!clip){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(clip)));
            setEditorOpen(true);
        }
    }, [])
    const onImgClipClick = useCallback((clip: Clip) => {
        if (!clip){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(clip)));
            setEditorOpen(true);
        }
    }, [])

    return (
        <>
            <ErrorSnackBar autoHideDuration={5000} open={loadError.error} setClose={() => setLoadError({ message: '', error: false })} errorMessage={loadError.message} />
            <StyledPopup isOpen={isOpen} onClose={onClose} width='70vw' height='85vh' title="Select clips to create highlight">
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                    <Box style={{ paddingTop: theme.spacing.md, display: 'flex', justifyContent: 'space-between' }}>
                        <StyledDropdown
                            value={selectedProdId}
                            onChange={handleProdIdChange}
                            id='create-highlight'
                            label='Job extraction schedule'
                            options={productionIds}
                            displayField='label'
                            valueField='id'
                        />
                        <StyledSwitch
                            checked={onlySelected}
                            label="Only Selected"
                            onChange={handleOnlySelected}
                        />
                    </Box>
                    <Box sx={{ color: theme.palette.lightGrey, flex: 1, overflow: 'hidden', paddingTop: theme.spacing.md }}>
                        <Box sx={{ overflowY: 'auto', height: '100%', overflowX:'hidden' }}>
                            {getClipsArray().map((clip, clipIndex) => (
                                <Grid container key={selectedProdId.id + clip.id}>
                                    <Grid item xs={12} lg={2} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                                        <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column', justifyContent: 'center' }}>
                                            <StyledCheckBox
                                                checked={selectedClips.has(clip.id)}
                                                onChange={() => { handleClipSelection(clipIndex) }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} lg={10}>
                                        <ClipListElem onClipImageClick={onImgClipClick} onClipTitleClick={onClipClick} clip={clip} displayMoreInfoIcon={false} noMarginTop={false} />
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>
                    </Box>
                    <StyledCollapse title={'Highlights Settings'} onOpen={() => setIsHighlightSettingOpen(true)} onClose={() => setIsHighlightSettingOpen(false)}>
                        {moreHighlightTypes && <Grid container sx={{ marginTop: theme.spacing.sm }} width={'100%'}>
                             <Grid item xs={12} sm={12} md={6} lg={6} >
                                <StyledDropdown
                                    value={selectedHighlight}
                                    paddingBottom={theme.spacing.sm}
                                    options={visibleHighlightTypes}
                                    label="Highlight Type"
                                    id='outlined'
                                    displayField={"label"}
                                    valueField={"type"}
                                    fullWidth={true}
                                    onChange={(e) => handleChangeHighlightType(e.target.value)}
                                />
                            </Grid>
                            {selectedHighlight.type === 'Pistolero' && <Grid item xs={12} sm={12} md={6} lg={6} paddingLeft={theme.spacing.sm} >
                                <StyledDropdown
                                    value={pistolero}
                                    paddingBottom={theme.spacing.sm}
                                    options={pistoleroOptions}
                                    label="Pistolero"
                                    id='outlined'
                                    fullWidth={true}
                                    onChange={(e) => setPistolero(e.target.value)}
                                />
                            </Grid>}
                        </Grid>}
                        <Box sx={{ marginTop: theme.spacing.sm }}>
                            <OpenerCloserComponent
                                selectedCompetition={selectedCompetition}
                                setSelectedCompetition={setSelectedCompetition}
                                dynamicCompetitions={true}
                                selectedItemsCompetitions={[]}
                                multi_event={false} selectedOpener={selectedOpener}
                                selectedCloser={selectedCloser}
                                setSelectedOpener={setSelectedOpener}
                                setSelectedCloser={setSelectedCloser}
                                isCollapseOpen={isHighlightSettingOpen}
                            />
                        </Box>
                        {selectedHighlight.type !== 'Fast' && <Box>
                            <AudioFadingCollapse
                                fade={audioFading}
                                fadeDuration={audioFadingDuration}
                                fadeDurationStep={fadeDurationStep}
                                maxFadeDuration={maxAudioFadeDuration}
                                minFadeDuration={minAudioFadeDuration}
                                onFadeChange={setAudioFading}
                                onFadeDurationChange={(value) => setAudioFadingDuration(value)}
                            />
                        </Box>}
                        {selectedHighlight.type === 'Fast' && <Box marginTop={theme.spacing.sm}>
                            <SongSelection
                                music={selectedSong ? selectedSong : null}
                                onSongChange={(song) => setSelectedSong(song)}
                            />
                        </Box>}
                    </StyledCollapse>
                    <Box sx={{ marginTop: theme.spacing.md }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '40px' }}>
                            <TransparentButton onClick={() => handleSetAll(true)} text={'Select All'} />
                            <TransparentButton onClick={() => handleSetAll(false)} text={'Deselect All'} />
                            <TransparentButton
                                disabled={ selectedHighlight.type === 'Pistolero' ? selectedHighlight.type === 'Pistolero' && !pistolero : false }
                                onClick={handleAutoSelection}
                                text={'Auto'} />
                            <Box sx={{ width: '300px' }}>
                                {selectedHighlight?.type === 'Regular' && <StyledRangeSlider
                                    value={autoSelectionDuration}
                                    onChange={handleHighlightDurationChange}
                                    min={minDuration}
                                    max={maxDuration}
                                    centredInterval={true}
                                    valueLabelDisplay="auto"
                                    formatLabel={(value) => formatDuration(Math.abs(value))}
                                    step={10}
                                />}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingTop: theme.spacing.md }}>
                            <Grid container>
                                <Grid item md={12} lg={6} />
                                <Grid item md={12} lg={6}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <Typography sx={{ paddingTop: theme.spacing.sm, fontSize: theme.fontSizes.subParagraphTitle, fontWeight: theme.fontWeights.subParagraphTitle, color: theme.palette.white }}>{"Highlight Duration: " + getHighlightDuration()}</Typography>
                                        <Typography sx={{ paddingTop: theme.spacing.sm, fontSize: theme.fontSizes.subParagraphTitle, fontWeight: theme.fontWeights.subParagraphTitle, color: theme.palette.white }} >{"# Clip: " + getHighlightNumber()}</Typography>
                                        <Box ref={createHighlightButtonRef} >
                                            <TransparentButton onClick={handleHighlightCreation} disabled={isCreateButtonDisabled()} text={'Create Highlight'} />
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Box>
            </StyledPopup>
            <WarningPopover
                onClose={() => setShowEditingWarning(false)}
                anchorEl={anchorEl}
                anchorOrigin={{vertical: 'top', horizontal: 'center',}}
                transformOrigin={{vertical: 'bottom', horizontal: 'center',}}
                buttonText={'Continue anyway'}
                onConfirm={executeCreateHighlight}
                message={'⚠️ Warning: One or more of the selected clips are currently being processed. The generated Highlight may not include the clips in their most recently saved state.'}
            />

            <StyledSnackbar
                open={isClipDownloading === 'DOWNLOAD'}
                setClose={() => setIsClipDownloading('NODOWNLOAD')}
                message={'Wait for your download'}
                severity={'info'}
                closeButton={true}
            />
            <StyledSnackbar
                open={isClipDownloading === 'FAILED'}
                setClose={() => setIsClipDownloading('NODOWNLOAD')}
                message={'Error to download your clip'}
                severity={'error'}
                closeButton={true}
            />

            <EditorPopup
                setExternalError={setLoadError}
                clip={editorClip}
                isOpen={editorOpen}
                handleClose={closeEditor}
                setIsClipDownloading={setIsClipDownloading}
                contentType={'clip'}
            />

        </>
    );
};

export default HighlightPopup;